import React, { useState } from 'react';
import { getStatusChipSx } from '../../../constants/statusColors';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  DeleteOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  PersonOutlined as PersonIcon,
} from '@mui/icons-material';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import AddMemberModal from '../../../components/committees/AddMemberModal';
import { csAdminApi } from '../../../services/api';

const MembersTab = ({ committee, onRefresh }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const roleColors = {
    CHAIRPERSON: { bg: '#FFEBEE', text: '#D32F2F' },
    CHAIRMAN: { bg: '#FFEBEE', text: '#D32F2F' },
    SECRETARY: { bg: '#E3F2FD', text: '#1976D2' },
    MEMBER: { bg: '#F5F5F5', text: '#757575' },
    ADVISOR: { bg: '#FFF3E0', text: '#E65100' },
    OBSERVER: { bg: '#F5F5F5', text: '#9E9E9E' },
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (member) => {
    setMemberToEdit(member);
    setEditModalOpen(true);
  };

  const handleEditMember = (memberData) => {
    setSnackbar({
      open: true,
      message: `${memberData.fullName}'s details have been updated`,
      severity: 'success',
    });
    setEditModalOpen(false);
    setMemberToEdit(null);
    if (onRefresh) onRefresh();
  };

  const handleConfirmDelete = async () => {
    try {
      await csAdminApi.removeCommitteeMember(committee.id, memberToDelete.id);

      setSnackbar({
        open: true,
        message: `${memberToDelete.name || memberToDelete.fullName} removed from the committee`,
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      
      // Refresh data
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to remove member. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">Committee Members</Typography>
          <Typography variant="body2" color="textSecondary">
            Total: {committee.members?.length || 0} members
          </Typography>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Joined Date</TableCell>
              <TableCell>Term Start</TableCell>
              <TableCell>Term End</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {committee.members && committee.members.length > 0 ? (
              committee.members.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          bgcolor: '#E3F2FD',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 24, color: '#1976D2' }} />
                      </Box>
                      <Typography variant="body2" fontWeight={500}>
                        {member.name || member.fullName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={member.role?.toUpperCase() || 'MEMBER'}
                      size="small"
                      sx={{
                        bgcolor: (roleColors[member.role?.toUpperCase()] || roleColors.MEMBER).bg,
                        color: (roleColors[member.role?.toUpperCase()] || roleColors.MEMBER).text,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '12px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {member.joinedDate ? new Date(member.joinedDate).toLocaleDateString('en-GB') : 
                     member.appointmentDate ? new Date(member.appointmentDate).toLocaleDateString('en-GB') : '-'}
                  </TableCell>
                  <TableCell>
                    {member.termStart ? new Date(member.termStart).toLocaleDateString('en-GB') :
                     member.appointmentDate ? new Date(member.appointmentDate).toLocaleDateString('en-GB') : '-'}
                  </TableCell>
                  <TableCell>
                    {member.termEnd ? new Date(member.termEnd).toLocaleDateString('en-GB') :
                     member.termEndDate ? new Date(member.termEndDate).toLocaleDateString('en-GB') : 'Indefinite'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={member.status?.toUpperCase() || 'ACTIVE'}
                      size="small"
                      sx={{
                        ...getStatusChipSx(member.status, { treatMissingAsActive: true }),
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditClick(member)}
                      title="Edit Member"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteClick(member)}
                      title="Remove Member"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No members assigned yet
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Committee Member"
        entityName={memberToDelete?.name || memberToDelete?.fullName}
        entityType="member"
        impacts={[
          'Remove member from this committee',
          'Retain member as director in the system',
          'Historical records will be preserved',
          'This action cannot be undone',
        ]}
      />

      {/* Edit Member Modal */}
      <AddMemberModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setMemberToEdit(null);
        }}
        onAdd={handleEditMember}
        committeeId={committee?.id}
        committeeName={committee?.name}
        existingMembers={committee?.members || []}
        editMember={memberToEdit}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default MembersTab;
